import {CircularProgress, Typography} from '@mui/material'
import React, {useCallback, useContext, useLayoutEffect, useState} from 'react'
import {AppContext} from '../contexts/AppContextProvider'
import http from '../http'
import {CustomerData} from "../contexts/types";
import useLogger from '../hooks/useLogger'
import {OrgCustomizationContext} from "../themes/OrgCustomizationProvider";
import useRedirect from "../hooks/useRedirect";
import {getSearchParamsCountryCode} from "../lib/locale-helpers";
import {patchGuestInfo} from "../contexts/GuestInfoReducer";

type DataRetrievalProps = {
    shouldRetrieveData?: boolean
}

const DataRetrieval: React.FC<DataRetrievalProps> = ({children, shouldRetrieveData = true}) => {
    const {complaint, dispatch, loggedIn, customer, setCustomer, setAddresses} = useContext(AppContext)
    const {t, orgName} = useContext(OrgCustomizationContext)
    const {redirect} = useRedirect()
    const log = useLogger()
    const [valid, setValid] = useState(false)
    const {isDevelopmentEnvironment, changeLanguage} = useContext(OrgCustomizationContext)

    const handleRedirect = async (loggedIn: boolean) => {
        let message = ''
        let UserType = ''
        const token = localStorage.getItem('token')

        if (loggedIn) {
            UserType = 'SignedIn'
            message = `userInfo: ${JSON.stringify(customer)}, token: ${token}`
        } else {
            UserType = 'GuestUser'
            message = `userInfo: Not Available , token: ${token}`
        }

        await http.post('api/Client/LogRedirect', {
            href: window.location.href,
            UserType,
            message,
            Date: Date()
        }).then(() => {
            redirect('login')
        })
    }

    const collectCustomerInformation = useCallback(() => {
        setValid(false)

        const hasSearchParams = window.location.search !== ''

        sessionStorage.removeItem('email')

        if (shouldRetrieveData && loggedIn) {
            http.get<CustomerData>(`/api/Customer`)
                .then(({data}) => {
                    setCustomer(data.customerDetails)
                    changeLanguage(data.customerDetails.language || 'en')
                    document.cookie = `X-User-Id=${data.customerDetails.customer}; path=/; SameSite=Strict`;
                    document.cookie = `X-User-Email=${data.customerDetails.emailAddress}; path=/; SameSite=Strict`;

                    data.customerDetails.country !== '' && dispatch(patchGuestInfo.country(getSearchParamsCountryCode() || ''))
                    data.customerDetails.emailAddress && sessionStorage.setItem('email', data.customerDetails.emailAddress)

                    if (!isDevelopmentEnvironment && data.partnerAddress === null) {
                        throw new Error(`
                            Partner address doesn't exist.
                            The Complaints Application may behave unexpectedly.
                        `)
                    } else if (isDevelopmentEnvironment && data.partnerAddress === null) {
                        return setValid(true)
                    } else {
                        setAddresses(data.partnerAddress)
                        setValid(true)
                    }
                })
                .catch((error) => log.error(error))
        } else if (shouldRetrieveData && !loggedIn) {
            if (!hasSearchParams) {
                handleRedirect(loggedIn)
            } else {
                if (!complaint.guestInfo.country) dispatch(patchGuestInfo.country(getSearchParamsCountryCode() || ''))
                setValid(true)
            }
        }
    }, [])

    useLayoutEffect(() => {
        collectCustomerInformation()
    }, [])

    return valid
        ? <>{children}</>
        : <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100vw',
                height: '100vh',
                flexDirection: 'column',
                gap: 30,
            }}
        >
            <Typography variant='h4' color='primary'>
                {orgName} {t('gi_complaints')}
            </Typography>

            <CircularProgress size='3rem'/>

            <p>Loading...</p>
        </div>
}

export default DataRetrieval
